import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_DEDUCTION_CATEGORY,
  DELETE_MASTER_DATA_DEDUCTION_CATEGORIES,
  GET_MASTER_DATA_DEDUCTION_CATEGORIES,
  UPDATE_MASTER_DATA_DEDUCTION_CATEGORY,
} from "@src/domain/graphql/masterData";
import {
  MasterDataDeductionCategory,
  type IMasterDataDeductionCategory,
  type IMasterDataDeductionCategoryDeleteResult,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataDeductionCategoryDTO,
  DeleteMasterDataDeductionCategoriesDTO,
  GetMasterDataDeductionCategoriesDTO,
  UpdateMasterDataDeductionCategoryDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataDeductionCategoryRepository,
  MasterDataDeductionCategoriesListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
} from "./masterDataRepository.shared";

function mapCategoryItem(
  item: IMasterDataDeductionCategory,
): MasterDataDeductionCategory {
  return MasterDataDeductionCategory.from({
    categoryId: item.categoryId,
    categoryName: item.categoryName,
    categoryKey: item.categoryKey,
    isActive: item.isActive,
    isLinked: Boolean(item.isLinked),
    createdAt: item.createdAt ?? null,
    createdBy: item.createdBy ?? null,
  });
}

function toCategoryMutationParams(
  params: CreateMasterDataDeductionCategoryDTO | UpdateMasterDataDeductionCategoryDTO,
) {
  return {
    categoryName: params.categoryName,
    categoryKey: params.categoryKey,
    isActive: params.isActive,
  };
}

export class MasterDataDeductionCategoryRepository
  implements IMasterDataDeductionCategoryRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getDeductionCategories(
    params: GetMasterDataDeductionCategoriesDTO,
  ): Promise<MasterDataDeductionCategoriesListResult> {
    const { data, error } = await this.client.query<{
      masterDataDeductionCategories: {
        items: IMasterDataDeductionCategory[];
      };
    }>({
      query: GET_MASTER_DATA_DEDUCTION_CATEGORIES,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive ?? true,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataDeductionCategories;
    if (!response) {
      throw new Error("Failed to fetch master data deduction categories");
    }

    return {
      items: (response.items ?? []).map(mapCategoryItem),
    };
  }

  async createDeductionCategory(
    params: CreateMasterDataDeductionCategoryDTO,
  ): Promise<MasterDataDeductionCategory> {
    return this.putDeductionCategory(
      CREATE_MASTER_DATA_DEDUCTION_CATEGORY,
      toCategoryMutationParams(params),
      (data) => data?.createMasterDataDeductionCategory,
      "Failed to create deduction category",
    );
  }

  async updateDeductionCategory(
    params: UpdateMasterDataDeductionCategoryDTO,
  ): Promise<MasterDataDeductionCategory> {
    return this.putDeductionCategory(
      UPDATE_MASTER_DATA_DEDUCTION_CATEGORY,
      {
        categoryId: params.categoryId,
        ...toCategoryMutationParams(params),
      },
      (data) => data?.updateMasterDataDeductionCategory,
      "Failed to update deduction category",
    );
  }

  async deleteDeductionCategories(
    params: DeleteMasterDataDeductionCategoriesDTO,
  ): Promise<IMasterDataDeductionCategoryDeleteResult> {
    const result = await this.client.mutate<{
      deleteMasterDataDeductionCategories?: IMasterDataDeductionCategoryDeleteResult;
    }>({
      mutation: DELETE_MASTER_DATA_DEDUCTION_CATEGORIES,
      variables: {
        params: {
          categoryIds: params.categoryIds,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    const payload = requireMasterDataMutationData(
      result.data?.deleteMasterDataDeductionCategories,
      result.error,
      "Failed to delete deduction categories",
    );

    return {
      deletedCount: payload.deletedCount ?? 0,
      blockedCount: payload.blockedCount ?? 0,
      blockedIds: payload.blockedIds ?? [],
      message: payload.message ?? null,
    };
  }

  private async putDeductionCategory(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataDeductionCategory?: IMasterDataDeductionCategory;
            updateMasterDataDeductionCategory?: IMasterDataDeductionCategory;
          }
        | null
        | undefined,
    ) => IMasterDataDeductionCategory | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataDeductionCategory> {
    const result = await this.client.mutate<{
      createMasterDataDeductionCategory?: IMasterDataDeductionCategory;
      updateMasterDataDeductionCategory?: IMasterDataDeductionCategory;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapCategoryItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
